import { memo, useMemo } from "react";

import { oids } from "@/configs/Settings";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { buildScheduleManagerRequesterReference } from "@/services/crm/triggerClinicalAutomation";
import { SendWhatsAppMessageDialog } from "src-next/modules/crm-clinical/messaging";

import BirthdayDashboardAlert from "./BirthdayDashboardAlert";
import { resolveCrmOrganizationId } from "../helpers/resolveCrmOrganizationId";
import { useBirthdayDashboardState } from "../hooks/useBirthdayDashboardState";
import { useCommunicationsWhatsAppSendDialog } from "../hooks/useCommunicationsWhatsAppSendDialog";

/**
 * Seção auto-contida de aniversariantes para o dashboard profissional (legado).
 * Encapsula hooks de sessão, pacientes e envio WhatsApp.
 */
const BirthdayDashboardSection = memo(() => {
  const { access, user } = useSessionContext();
  const { patients: patientEntries, patientOrganizationLoading } =
    usePatientOrganizationContext();

  const organizationId = useMemo(
    () => resolveCrmOrganizationId(access?.organizationId),
    [access?.organizationId],
  );

  const practitionerId = useMemo(
    () => (user?.username ? `${oids.cpf}-${user.username}` : undefined),
    [user?.username],
  );

  const requesterReference = useMemo(
    () =>
      buildScheduleManagerRequesterReference({
        organizationId: access?.organizationId,
        practitionerUserId: user?.username
          ? `${oids.cpf}-${user.username}`
          : null,
      }),
    [access?.organizationId, user?.username],
  );

  const birthdayState = useBirthdayDashboardState({
    organizationId,
    practitionerId,
    requesterReference,
    patientEntries,
    patientOrganizationLoading,
  });

  const { openSendMessage, sendDialogProps } = useCommunicationsWhatsAppSendDialog(
    {
      organizationId,
      practitionerId,
      onMessageSent: birthdayState.refetchCommunications,
    },
  );

  if (!birthdayState.shouldShowAlert) return null;

  return (
    <>
      <BirthdayDashboardAlert
        state={birthdayState}
        onOpenSendMessage={openSendMessage}
      />

      {sendDialogProps?.open ? (
        <SendWhatsAppMessageDialog {...sendDialogProps} />
      ) : null}
    </>
  );
});

BirthdayDashboardSection.displayName = "BirthdayDashboardSection";

export default BirthdayDashboardSection;
